import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

interface Props {
  children: ReactNode;
  label?: string;
}

interface State {
  error: Error | null;
}

export default class WorkspaceErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`Workspace error (${this.props.label || 'workspace'}):`, error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="h-full min-h-[200px] flex flex-col items-center justify-center gap-3 p-6 text-center bg-background">
          <AlertTriangle className="w-8 h-8 text-destructive" />
          <div>
            <p className="text-sm font-semibold text-foreground">Something went wrong loading this view</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-md">{this.state.error.message}</p>
          </div>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90"
          >
            Reload page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
